/**
 * Format: Carousel product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API response for carousel.
 *
 */
import { formatProduct } from '~/format/format-product';
import { decodeApiId, transformEdges } from '~/helpers/graphql';

/**
 * Format products used in the product carousel section.
 * - Products may come from metaobject references or recommendation API.
 * @param {Object} response - GraphQL response.
 * @param {Number} [limit] - Maximum number of products to return, used for
 * queries which don't support limit parameter.
 * @returns {Object}
 */
export function formatCarouselProducts(response, limit = 12) {
  if (!response) {
    return { products: [] };
  }

  // Last seen and last ordered products
  if (response.nodes) {
    const isVariant = response.nodes.every((node) => node?.product);

    if (isVariant) {
      return {
        isVariant: true,
        products: response.nodes
          .filter(Boolean)
          .map((node) =>
            formatProduct(node.product, { variantId: decodeApiId(node.id) }),
          )
          .slice(0, limit),
      };
    }

    return {
      products: response.nodes.map(formatProduct).slice(0, limit),
    };
  }

  // Product recommendations
  if (response.recommendations) {
    return {
      products: response.recommendations.map(formatProduct).slice(0, limit),
    };
  }

  // Metaobject collection reference
  if (response.metaobject?.collection !== null) {
    return {
      collection: {
        title: response.metaobject?.collection?.reference?.title,
        handle: `//${location.host}${cnvs.routes.normalisedRoot}collections/${response.metaobject?.collection?.reference?.handle}`,
      },
      products: transformEdges(
        response.metaobject?.collection?.reference?.products,
      ).map(formatProduct),
    };
  }

  // Metaobject product list reference
  if (response.metaobject?.productList !== null) {
    return {
      products: transformEdges(
        response.metaobject?.productList?.references,
      ).map(formatProduct),
    };
  }

  // Metaobject variant list reference
  return {
    isVariant: true,
    products: transformEdges(response.metaobject?.variantList?.references).map(
      (node) =>
        formatProduct(node.product, {
          variantId: decodeApiId(node.id),
          variantTitle: node.title,
          variantImage: node.image,
          variantAvailable: node.availableForSale,
        }),
    ),
  };
}
